import styled, { css } from "styled-components";

import { fontSizes } from "../../shared/styles/typography";
import { pageRoot } from "../../styles/pageRoot";

export const Container = styled.div`
  ${pageRoot}
  background-color: var(--color-background);
  color: var(--color-text);
`;

export const Main = styled.main`
  max-width: 800px;
  margin: 0 auto;
  padding: 2rem 1rem;
`;

export const Header = styled.div`
  text-align: center;
  margin-bottom: 2rem;

  h2 {
    font-size: ${fontSizes.xl}px;
    font-weight: var(--font-weight-bold);
    margin-bottom: 0.5rem;
  }

  p {
    color: var(--color-text-secondary);
    font-size: ${fontSizes.sm}px;
  }
`;

export const LeagueInfoCard = styled.div`
  background: linear-gradient(
    135deg,
    var(--color-primary),
    var(--color-primary-dark)
  );
  border-radius: 1.25rem;
  padding: 1.5rem;
  color: white;
  margin-bottom: 2.5rem;
  display: flex;
  align-items: center;
  gap: 1.5rem;
  box-shadow: 0 10px 15px -3px rgba(var(--color-primary-rgb), 0.3);

  .league-icon {
    width: 64px;
    height: 64px;
    background-color: rgba(255, 255, 255, 0.2);
    border-radius: 1rem;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .info {
    flex: 1;
    h3 {
      font-size: ${fontSizes.base}px;
      font-weight: var(--font-weight-semibold);
      margin-bottom: 0.25rem;
    }
    p {
      opacity: 0.9;
      font-size: ${fontSizes.sm}px;
    }
  }

  .stats {
    text-align: right;
    .position {
      font-size: ${fontSizes.xl}px;
      font-weight: var(--font-weight-bold);
    }
    .total {
      font-size: ${fontSizes.sm}px;
      opacity: 0.8;
    }
  }
`;

export const PodiumContainer = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: center;
  gap: 1rem;
  margin-bottom: 3rem;
  padding: 0 1rem;

  @media (max-width: 480px) {
    gap: 0.5rem;
  }
`;

const podiumItemStyles = {
  1: css`
    height: 160px;
    background: linear-gradient(to bottom, #ffd700, #ffa500);
    order: 2;
  `,
  2: css`
    height: 130px;
    background: linear-gradient(to bottom, #c0c0c0, #a8a8a8);
    order: 1;
  `,
  3: css`
    height: 110px;
    background: linear-gradient(to bottom, #cd7f32, #b87333);
    order: 3;
  `,
};

export const PodiumItem = styled.div<{ position: 1 | 2 | 3 }>`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
  ${(props) => podiumItemStyles[props.position]}
  border-top-left-radius: 1rem;
  border-top-right-radius: 1rem;
  position: relative;
  padding-top: 1.5rem;
  color: white;
  cursor: pointer;
  outline: none;

  &:focus-visible {
    box-shadow: 0 0 0 3px rgba(255, 255, 255, 0.55);
  }

  .avatar-wrapper {
    position: absolute;
    top: -45px;
    width: ${(props) => (props.position === 1 ? "80px" : "64px")};
    height: ${(props) => (props.position === 1 ? "80px" : "64px")};
    border-radius: 50%;
    border: 4px solid
      ${({ position }) => {
        if (position === 1) return "#ffd700";
        if (position === 2) return "#c0c0c0";
        return "#cd7f32";
      }};
    overflow: hidden;
    background-color: var(--color-surface);

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }

    .initials {
      width: 100%;
      height: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
      font-weight: var(--font-weight-semibold);
      color: var(--color-text-secondary);
      font-size: ${(props) =>
        props.position === 1 ? fontSizes.xl : fontSizes.base}px;
    }
  }

  .name {
    font-weight: var(--font-weight-semibold);
    font-size: ${fontSizes.sm}px;
    max-width: 90%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    margin-top: 1rem;
  }

  .xp {
    font-size: ${fontSizes.sm}px;
    font-weight: 600;
    opacity: 0.9;
  }

  .rank-icon {
    margin-top: auto;
    margin-bottom: 1rem;
  }
`;

export const RankingList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

export const RankingItem = styled.div<{ isUser: boolean }>`
  background-color: ${(props) =>
    props.isUser
      ? "rgba(var(--color-primary-rgb), 0.1)"
      : "var(--color-surface)"};
  border: 1px solid
    ${(props) =>
      props.isUser ? "var(--color-primary)" : "var(--color-border)"};
  border-radius: 1rem;
  padding: 1rem;
  display: flex;
  align-items: center;
  gap: 1rem;
  transition: transform 0.2s;
  cursor: pointer;
  outline: none;

  &:hover {
    transform: translateX(4px);
  }

  &:focus-visible {
    box-shadow: 0 0 0 2px var(--color-primary);
  }

  .index {
    width: 32px;
    font-weight: var(--font-weight-bold);
    font-size: ${fontSizes.base}px;
    color: var(--color-text-secondary);
    text-align: center;
  }

  .avatar {
    width: 44px;
    height: 44px;
    border-radius: 50%;
    overflow: hidden;
    background-color: var(--color-background);
    flex-shrink: 0;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }

    .initials {
      width: 100%;
      height: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
      font-weight: var(--font-weight-semibold);
      color: var(--color-text-secondary);
      font-size: ${fontSizes.sm}px;
    }
  }

  .user-info {
    flex: 1;
    .name {
      font-weight: var(--font-weight-semibold);
      color: var(--color-text);
    }
    .league-tag {
      font-size: ${fontSizes.sm}px;
      color: var(--color-text-secondary);
    }
  }

  .xp-stats {
    text-align: right;
    .xp-value {
      font-weight: var(--font-weight-bold);
      color: var(--color-primary);
    }
    .xp-label {
      font-size: ${fontSizes.xs}px;
      color: var(--color-text-secondary);
      text-transform: uppercase;
    }
  }
`;

export const WeekInfoBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem 1rem;
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: 1rem;
  padding: 0.85rem 1.1rem;
  margin-bottom: 2rem;
  font-size: ${fontSizes.sm}px;

  .label {
    font-weight: var(--font-weight-semibold);
    color: var(--color-text-secondary);
    text-transform: uppercase;
    font-size: ${fontSizes.xs}px;
    letter-spacing: 0.06em;
  }

  .value {
    flex: 1;
    color: var(--color-text);
    font-weight: var(--font-weight-semibold);
  }

  .xp {
    color: var(--color-text-secondary);
    font-size: ${fontSizes.sm}px;
  }
`;

export const XpLegendCard = styled.div`
  margin-top: 2rem;
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: 1.25rem;
  padding: 1.25rem 1.35rem;

  .legend-header {
    display: flex;
    align-items: center;
    gap: 0.6rem;
    margin-bottom: 1rem;
    color: var(--color-primary);

    h4 {
      margin: 0;
      font-size: ${fontSizes.base}px;
      font-weight: var(--font-weight-semibold);
      color: var(--color-text-title);
    }
  }

  ul {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 0.65rem;
  }

  li {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 1rem;
    font-size: ${fontSizes.sm}px;
    color: var(--color-text-secondary);

    strong {
      color: var(--color-primary);
      font-weight: var(--font-weight-bold);
      white-space: nowrap;
    }
  }
`;

export const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 0.75rem;
  padding: 2.5rem 1.25rem;
  margin-bottom: 1.5rem;

  .icon-wrap {
    width: 80px;
    height: 80px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    background: color-mix(
      in srgb,
      var(--color-primary) 12%,
      var(--color-surface)
    );
    margin-bottom: 0.5rem;
  }

  h3 {
    margin: 0;
    font-size: ${fontSizes.xl}px;
    font-weight: var(--font-weight-bold);
    color: var(--color-text-title);
  }

  p {
    margin: 0;
    max-width: 320px;
    font-size: ${fontSizes.sm}px;
    line-height: 1.45;
    color: var(--color-text-secondary);
  }
`;
